/**
 * ───────────────────────────────────────────────────────────────────────────
 * MEMBERSHIP VALUE — single source of truth for every savings claim.
 * ───────────────────────────────────────────────────────────────────────────
 * Every number shown on the homepage membership surfaces (hero strip, role
 * card "Members" line, "Why become a member" section, CtaBand, FAQ) is DERIVED
 * here from LEAD_PRICE / PLAN_PRICES / PLAN_INCLUDED_LEADS in
 * src/data/constants.ts. Nothing is retyped in a component, so if a price or
 * an allowance changes, every claim on the page changes with it and the "60%"
 * can never go stale.
 *
 * HONESTY RULE (approved 2026-10-14, docs/HOMEPAGE_MEMBERSHIP_PROPOSAL_20261014.md):
 * only benefits that exist today are advertised —
 *   - monthly lead credits (granted by payment-webhook)
 *   - lower effective cost per lead (pure math below)
 *   - FREE business website on Premium (mkt_website_requests)
 *   - MEMBER-FIRST JOB ALERTS (live 2026-10-14, job-alerts-digest v3.1):
 *     active members' alert digests include brand-new matching projects on the
 *     next hourly run; non-members only receive projects older than
 *     mkt_settings['job_alert_tiers'].nonmember_delay_hours (default 4, admin
 *     editable, never below 1 from the admin UI so this claim stays true).
 *     The copy never states the hour count, so it cannot drift from the setting.
 * NOT advertised until built: featured profile, priority placement, SMS lead alerts.
 */
import {
  LEAD_PRICE,
  PLAN_CODES,
  PLAN_PRICES,
  PLAN_INCLUDED_LEADS,
  PlanCode,
  Lang,
  money,
  planLabel,
  planTermMonths,
  planTermTotal,
} from '@/data/constants';

/** Effective price per lead for a plan, in cents (rounded): 1980 / 1990 / 1996. */
export const perLeadCents = (code: string): number => {
  const leads = PLAN_INCLUDED_LEADS[code] || 0;
  const price = PLAN_PRICES[code] || 0;
  return leads > 0 ? Math.round(price / leads) : LEAD_PRICE;
};

/** Cheapest effective per-lead price across all plans (cents). */
export const lowestPerLeadCents = (): number =>
  Math.min(...PLAN_CODES.map((c) => perLeadCents(c)));

/** Plan with the cheapest effective per-lead price. */
export const lowestPerLeadPlan = (): PlanCode =>
  [...PLAN_CODES].sort((a, b) => perLeadCents(a) - perLeadCents(b))[0];

/** Savings vs a single lead, in whole percent, rounded DOWN so we never overstate. */
export const savingsPct = (code: string): number =>
  Math.max(0, Math.floor((1 - perLeadCents(code) / LEAD_PRICE) * 100));

/** Headline savings % — the smallest saving across plans, so it is true for every plan. */
export const memberSavingsPct = (): number => Math.min(...PLAN_CODES.map((c) => savingsPct(c)));

/** Smallest n where n single leads cost MORE than one month of the plan: 2 / 4 / 10. */
export const breakEvenLeads = (code: string): number => {
  const price = PLAN_PRICES[code] || 0;
  return Math.floor(price / LEAD_PRICE) + 1;
};

/** Cost of buying the plan's monthly allowance as single leads (cents). */
export const singleCostForAllowance = (code: string): number =>
  (PLAN_INCLUDED_LEADS[code] || 0) * LEAD_PRICE;

/** Plans that include the FREE business website (real: mkt_website_requests). */
export const PLAN_INCLUDES_WEBSITE: Record<string, boolean> = { basic: false, pro: false, premium: true };

const ordinal = (n: number, lang: Lang): string => {
  if (lang === 'es') return `${n}.º`;
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** "pays for itself at your 2nd lead" */
export const breakEvenLine = (code: string, lang: Lang): string => {
  const n = breakEvenLeads(code);
  return lang === 'es'
    ? `Se paga solo a partir de su ${ordinal(n, lang)} lead del mes`
    : `Pays for itself by your ${ordinal(n, lang)} lead of the month`;
};

/**
 * The always-visible disclosure next to any savings claim. Premium's term is
 * derived (never retyped) so it matches planTermNote().
 */
export const membershipDisclosure = (lang: Lang): string => {
  const committed = PLAN_CODES.filter((c) => planTermMonths(c) > 1);
  const parts = committed.map((c) => {
    const months = planTermMonths(c);
    const total = money(planTermTotal(c, PLAN_PRICES[c]));
    return lang === 'es'
      ? `${planLabel(c, lang)} requiere un acuerdo de ${months} meses (${total} en total, cobrado mes a mes — no por adelantado).`
      : `${planLabel(c, lang)} requires a ${months}-month agreement (${total} total, billed monthly — not upfront).`;
  });
  const base =
    lang === 'es'
      ? 'Los créditos no usados expiran al final de cada ciclo de facturación y no se acumulan.'
      : 'Unused credits expire at the end of each billing cycle and do not roll over.';
  return [base, ...parts].join(' ');
};

/**
 * All homepage membership copy, bilingual, every figure derived above.
 * Components render these strings; they never build a price themselves.
 */
export const membershipCopy = (lang: Lang) => {
  const es = lang === 'es';
  const single = money(LEAD_PRICE);
  const from = money(lowestPerLeadCents());
  const pct = memberSavingsPct();
  const basic = 'basic';
  const basicName = planLabel(basic, lang);
  const basicPrice = money(PLAN_PRICES[basic]);
  const basicLeads = PLAN_INCLUDED_LEADS[basic];
  const be = breakEvenLeads(basic);
  const beCost = money(be * LEAD_PRICE);
  const approx = money(Math.round(lowestPerLeadCents() / 100) * 100);

  return {
    /** Hero strip (above the fold, visible on phones). */
    strip: es
      ? `Los miembros pagan desde ${from} por lead en vez de ${single} — ahorre ${pct}%.`
      : `Members pay from ${from} per lead instead of ${single} — save ${pct}%.`,
    stripCta: es ? 'Ver planes' : 'See plans',
    /** Hero sub (desktop). */
    heroSub: es
      ? `Los dueños de casa y de propiedades comerciales publican proyectos gratis. Contratistas y subcontratistas ven todos los trabajos gratis — y los miembros desbloquean leads por unos ${approx} en vez de ${single}.`
      : `Homeowners and commercial property owners post projects free. Contractors and subcontractors browse every job free — and members unlock leads for about ${approx} instead of ${single}.`,
    /** Role card "Members" lines. */
    cardLabel: es ? 'Miembros' : 'Members',
    cardContractor: es
      ? `leads desde ${from} (individual ${single}) · créditos cada mes`
      : `leads from ${from} (single ${single}) · credits every month`,
    cardSub: es
      ? `ahorre ${pct}% por lead · ${be} leads individuales (${beCost}) cuestan más que ${basicName}`
      : `save ${pct}% per lead · ${be} single leads (${beCost}) cost more than ${basicName}`,
    /** Section header. */
    sectionEyebrow: es ? 'Membresía' : 'Membership',
    sectionTitle: es ? '¿Por qué hacerse miembro?' : 'Why become a member?',
    sectionSub: es
      ? `Mirar es gratis para todos. Los miembros reciben créditos de lead cada mes a unos ${approx} cada uno en vez de ${single} — y los proyectos nuevos les llegan primero.`
      : `Browsing is free for everyone. Members get lead credits every month at about ${approx} each instead of ${single} — and new projects reach them first.`,
    /** Comparison table. */
    colSingle: es ? 'Lead individual' : 'Single lead',
    colMember: es ? `Miembro (${basicName})` : `Member (${basicName})`,
    rowPerLead: es ? 'Precio por lead' : 'Price per lead',
    rowBundle: es ? `${basicLeads} leads` : `${basicLeads} leads`,
    rowRefill: es ? 'Créditos nuevos cada mes' : 'Credits refill every month',
    rowCommit: es ? 'Compromiso' : 'Commitment',
    commitNone: es ? 'Ninguno' : 'None',
    commitMonthly: es ? 'Mes a mes' : 'Month to month',
    singlePerLead: single,
    memberPerLead: money(perLeadCents(basic)),
    singleBundle: money(singleCostForAllowance(basic)),
    memberBundle: `${basicPrice}${es ? '/mes' : '/mo'}`,
    /** Role "why it matters" bullets — true only. */
    whyContractorTitle: es ? 'Contratistas' : 'Contractors',
    whyContractor: es
      ? 'Llene huecos en su agenda con proyectos residenciales y comerciales a un costo por lead predecible.'
      : 'Fill gaps in your schedule with homeowner and commercial projects at a predictable cost per lead.',
    whySubTitle: es ? 'Subcontratistas' : 'Subcontractors',
    whySub: es
      ? `Trabaje su oficio y su área por unos ${approx} por lead — sin subastas, sin recargos.`
      : `Work your trade and area for about ${approx} a lead — no bidding, no surge pricing.`,
    ctaPlans: es ? 'Comparar planes' : 'Compare plans',
    ctaSingle: es ? 'Empezar con un lead' : 'Start with one lead',
    popular: es ? 'Más popular' : 'Most popular',
    websiteIncluded: es ? 'Sitio web de negocio GRATIS incluido' : 'FREE business website included',
    leadsPerMonth: es ? 'leads al mes' : 'leads per month',
    perLeadSuffix: es ? 'por lead' : 'per lead',
    savePct: (code: string) => (es ? `Ahorra ${savingsPct(code)}%` : `Save ${savingsPct(code)}%`),
    disclosure: membershipDisclosure(lang),
    /** CtaBand pill. */
    bandPrice: es
      ? `Lead individual ${single} · Miembros desde ${from}/lead — sin subastas, sin recargos`
      : `Single lead ${single} · Members from ${from}/lead — no bidding, no surge pricing`,
    bandSecondary: es ? 'Comparar planes' : 'Compare plans',
    /**
     * MEMBER-FIRST JOB ALERTS (live, job-alerts-digest v3.1). Never states an
     * hour count — the delay lives in mkt_settings and admins can change it.
     */
    memberFirst: es ? 'Los proyectos nuevos llegan primero a los miembros' : 'New projects reach members first',
    memberFirstDetail: es
      ? 'Sus alertas de trabajo por correo incluyen proyectos nuevos de su oficio y área de inmediato; los no miembros los reciben horas después.'
      : 'Your job alert emails include brand-new projects in your trade and area right away; non-members get them hours later.',
    rowFirst: es ? 'Proyectos nuevos en sus alertas' : 'New projects in your alerts',
    firstSingle: es ? 'Horas después' : 'Hours later',
    firstMember: es ? 'Primero' : 'First',
    /** Membership FAQ entry (also emitted in FAQPage schema). */
    faqQ: es ? '¿Qué gano al hacerme miembro?' : 'What do I get as a member?',
    faqA: es
      ? `Créditos de lead cada mes a unos ${approx} cada uno en vez de ${single} — ${be} leads individuales (${beCost}) ya cuestan más que el plan ${basicName} (${basicPrice}/mes con ${basicLeads} leads). Los proyectos nuevos llegan primero a los miembros: sus alertas de trabajo incluyen proyectos recién publicados de inmediato, mientras que los no miembros los reciben horas después. Premium además incluye un sitio web de negocio gratis. No necesita membresía para mirar o comprar un solo lead. ${membershipDisclosure(lang)}`
      : `Lead credits every month at about ${approx} each instead of ${single} — ${be} single leads (${beCost}) already cost more than the ${basicName} plan (${basicPrice}/mo with ${basicLeads} leads). New projects reach members first: your job alerts include newly posted projects right away, while non-members get them hours later. Premium also includes a free business website. You never need a membership to browse or to buy a single lead. ${membershipDisclosure(lang)}`,
    /** Shown instead of the strip to signed-in active members. */
    memberActive: (n: number, plan: string) =>
      es
        ? `Usted es miembro ${planLabel(plan, lang)} · ${n} crédito(s) disponible(s)`
        : `You're a ${planLabel(plan, lang)} member · ${n} credit${n === 1 ? '' : 's'} available`,
    memberActiveCta: es ? 'Usar mis créditos' : 'Use my credits',
  };
};
